import * as React from 'react';
import { useConfirmationValidation } from './useConfirmationValidation';
import { ILabel } from '../constants/interfaces';
import { FormState } from './useFormState';

/**
 * Type definition for modal queue items
 */
export interface IModalQueueItem {
  type: 'attachment' | 'action_required' | 'api_rules';
  data: unknown;
  index?: number;
}

/**
 * Custom hook for managing modal validation state and sequential modal processing
 */
export function useModalValidation(
  formState: FormState,
  clearFormData: () => void,
  labels: ILabel[],
  setSnackbar?: (snackbar: {
    message: string;
    open: boolean;
    severity: 'success' | 'error';
  }) => void
) {
  const [modalTrigger, setModalTrigger] = React.useState<
    'submit' | 'input' | 'error' | null
  >(null);
  const [pendingModals, setPendingModals] = React.useState<IModalQueueItem[]>(
    []
  );
  const [isProcessing, setIsProcessing] = React.useState(false);
  const confirmation = useConfirmationValidation(setSnackbar);

  const formStateRef = React.useRef(formState);
  const handleNormalSubmitRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  const processNextModal = React.useCallback(async () => {
    if (isProcessing || pendingModals.length === 0) return;

    setIsProcessing(true);
    const nextModal = pendingModals[0];

    try {
      if (
        nextModal.type === 'action_required' ||
        nextModal.type === 'api_rules'
      ) {
        const validationRules = {
          [nextModal.type]: nextModal.data,
        };

        const isValid = await confirmation.validate(
          formState,
          validationRules,
          labels
        );

        // Remove the processed modal from queue
        setPendingModals((prev) => prev.slice(1));

        if (!isValid) {
          setModalTrigger('submit');
          setIsProcessing(false);
          return;
        }

        // Check if there are more modals
        const remainingModals = pendingModals.slice(1);
        if (remainingModals.length === 0) {
          // All done, submit
          if (handleNormalSubmitRef.current) {
            const submitFn = handleNormalSubmitRef.current;
            handleNormalSubmitRef.current = null;
            setModalTrigger(null);
            setIsProcessing(false);
            submitFn();
            return;
          }
        }

        setIsProcessing(false);
        // If there are more modals, this function will be called again
      } else {
        // Remove unknown modal types
        setPendingModals((prev) => prev.slice(1));
        setIsProcessing(false);
      }
    } catch (error) {
      // Clear everything on error
      setPendingModals([]);
      handleNormalSubmitRef.current = null;
      setIsProcessing(false);

      // Show error modal
      const errorModal = {
        canContinue: false,
        message: `API validation failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        shouldReset: false,
        title: 'Validation Error',
      };

      confirmation.setValidationResponse(errorModal);
      confirmation.setShowModal(true);
      setModalTrigger('error');

      if (setSnackbar) {
        setSnackbar({
          message: `API validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          open: true,
          severity: 'error',
        });
      }
    }
  }, [pendingModals, isProcessing, confirmation, formState, setSnackbar]);

  // Trigger processing when modals are added and no modal is showing
  React.useEffect(() => {
    if (pendingModals.length > 0 && !confirmation.showModal && !isProcessing) {
      processNextModal();
    }
  }, [pendingModals, confirmation.showModal, isProcessing, processNextModal]);

  const handleConfirm = React.useCallback(
    (handleNormalSubmit: () => void) => {
      if (modalTrigger === 'input') {
        confirmation.setShowModal(false);
        setModalTrigger(null);

        if (confirmation.validationResponse?.shouldReset) {
          clearFormData();
        }

        if (confirmation.validationResponse?.canContinue === false) {
          return;
        }
        return;
      }

      if (modalTrigger === 'error') {
        confirmation.setShowModal(false);
        setModalTrigger(null);
        return;
      }

      // For submit modals
      if (
        !handleNormalSubmitRef.current &&
        confirmation.validationResponse?.canContinue !== false
      ) {
        handleNormalSubmitRef.current = handleNormalSubmit;
      }

      confirmation.setShowModal(false);
      setModalTrigger(null);

      if (confirmation.validationResponse?.shouldReset) {
        clearFormData();
      }

      if (confirmation.validationResponse?.canContinue === false) {
        setPendingModals([]);
        handleNormalSubmitRef.current = null;
        setIsProcessing(false);
        return;
      }

      // If there are no pending modals left, submit the form
      if (pendingModals.length === 0 && handleNormalSubmitRef.current) {
        const submitFn = handleNormalSubmitRef.current;
        handleNormalSubmitRef.current = null;
        setTimeout(() => {
          submitFn();
        }, 0);
      }

      // Continue processing will happen automatically via useEffect
    },
    [confirmation, clearFormData, modalTrigger, pendingModals.length]
  );

  const handleInputChangeWithValidation = React.useCallback(
    (
      key: string | number,
      value: string | number | boolean,
      children: unknown,
      handleNormalInputChange: (
        key: string | number,
        value: string | number | boolean,
        children: unknown
      ) => void
    ) => {
      const labelId = String(key);
      const field = labels.find((l) => String(l.labelId) === labelId);

      if (field && field.rules) {
        try {
          const fieldRules = JSON.parse(field.rules);

          if (fieldRules.action) {
            const updatedFormState = {
              ...formStateRef.current,
              [labelId]: {
                ...formStateRef.current[labelId],
                children:
                  children || formStateRef.current[labelId]?.children || [],
                value,
              },
            };

            const validationRules = {
              action_required: fieldRules.action,
            };

            void confirmation
              .validate(updatedFormState, validationRules, labels)
              .then((isValid) => {
                if (!isValid) {
                  setModalTrigger('input');
                  return;
                }
                handleNormalInputChange(key, value, children);
              });
            return;
          }
        } catch (error) {
          console.warn(
            `Failed to parse field rules for field ${field.labelId}:`,
            error
          );
        }
      }

      handleNormalInputChange(key, value, children);
    },
    [labels, confirmation]
  );

  const handleSubmitWithValidation = React.useCallback(
    (
      rules: {
        attachment?: {
          warn?: { title?: string; message?: string; canContinue?: boolean };
        };
        action_required?: unknown;
        api_rules?: unknown;
      },
      selectedFiles: File[],
      handleNormalSubmit: () => void,
      visibleFields?: unknown[],
      fieldValidations?: (
        fields: unknown[],
        state: unknown
      ) => Record<string, string>,
      setErrors?: (errors: Record<string, string>) => void
    ) => {
      // Field validation first
      if (visibleFields && fieldValidations && setErrors) {
        const validationErrors = fieldValidations(visibleFields, formState);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
          return;
        }
      }

      handleNormalSubmitRef.current = handleNormalSubmit;

      // Check for attachment warning first
      if (selectedFiles.length === 0 && rules.attachment?.warn) {
        const warnRule = {
          canContinue: rules.attachment.warn.canContinue ?? true,
          message:
            rules.attachment.warn.message ||
            'The request is being submitted without supporting documents. Please confirm you would like to submit without attachments.',
          shouldReset: false,
          title: rules.attachment.warn.title || 'File Upload Confirmation',
        };

        const pendingModalsList: IModalQueueItem[] = [];
        if (rules.action_required) {
          pendingModalsList.push({
            data: rules.action_required,
            type: 'action_required',
          });
        }
        if (rules.api_rules) {
          pendingModalsList.push({ data: rules.api_rules, type: 'api_rules' });
        }
        setPendingModals(pendingModalsList);

        confirmation.setValidationResponse(warnRule);
        confirmation.setShowModal(true);
        setModalTrigger('submit');
        return;
      }

      // No attachment modal, process other rules
      if (rules.action_required || rules.api_rules) {
        const modalsList: IModalQueueItem[] = [];

        if (rules.action_required) {
          modalsList.push({
            data: rules.action_required,
            type: 'action_required',
          });
        }
        if (rules.api_rules) {
          modalsList.push({ data: rules.api_rules, type: 'api_rules' });
        }

        setPendingModals(modalsList);
        return;
      }

      // No modals needed, submit immediately
      handleNormalSubmit();
    },
    [formState, confirmation]
  );

  const handleCancel = React.useCallback(() => {
    confirmation.setShowModal(false);
    setModalTrigger(null);

    if (modalTrigger === 'submit' || modalTrigger === 'error') {
      setPendingModals([]);
      handleNormalSubmitRef.current = null;
      setIsProcessing(false);
    }

    if (confirmation.validationResponse?.shouldReset) {
      clearFormData();
    }
  }, [confirmation, clearFormData, modalTrigger]);

  const hasPendingModals = React.useMemo(() => {
    return pendingModals.length > 0 || confirmation.showModal || isProcessing;
  }, [pendingModals.length, confirmation.showModal, isProcessing]);

  return {
    confirmation,
    handleCancel,
    handleConfirm,
    handleInputChangeWithValidation,
    handleSubmitWithValidation,
    hasPendingModals,
    modalTrigger,
  };
}
